import { useCallback, useRef, useState } from "react";
import HistoricAnalysesAPI from "../../apis/historic-analyses/historicAnalysesAPI.js";

const EMPTY = { data: null, isLoading: false, error: null };

// onAnalysisDeleted(item) is called after a delete so the screen can close
// that analysis if it is open on the map or in the report preview
const useAnalysesHistory = (onAnalysisDeleted) => {
  const analysesHistoryApi = useRef(new HistoricAnalysesAPI()).current;
  const lastFilters = useRef({}); // reused to refresh the list after a delete

  const [stats, setStats] = useState(EMPTY);
  const [statsIds, setStatsIds] = useState([]); // [] = global stats
  const [statsCandidates, setStatsCandidates] = useState(EMPTY);
  const [analyses, setAnalyses] = useState(EMPTY);

  // ---------------------- global stats ---------------------------
  const fetchStats = useCallback(async () => {
    setStats((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await analysesHistoryApi.getHistoricStatsApi();
      if (response.status !== 200) throw new Error("Unexpected status");
      setStats({ data: response.data, isLoading: false, error: null });
      setStatsIds([]); // back to global
    } catch (err) {
      console.error("Stats error:", err);
      setStats((prev) => ({
        ...prev,
        isLoading: false,
        error: "Impossible de charger les statistiques.",
      }));
    }
  }, []);

  // ---------------------- stats for selected analyses ---------------------------
  const fetchStatsByIds = useCallback(async (ids) => {
    setStats((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await analysesHistoryApi.getStatsByIdsApi(ids);
      if (response.status !== 200 && response.status !== 201)
        throw new Error("Unexpected status");
      setStats({ data: response.data, isLoading: false, error: null });
      setStatsIds(ids);
    } catch (err) {
      console.error("Stats by ids error:", err);
      setStats((prev) => ({
        ...prev,
        isLoading: false,
        error: "Impossible de charger les statistiques sélectionnées.",
      }));
    }
  }, []);

  // ---------------------- candidates for the stats filter ---------------------------
  const fetchStatsCandidates = useCallback(async (filters = {}) => {
    setStatsCandidates((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await analysesHistoryApi.getCostsAnalysesApi(filters);
      if (response.status !== 200) throw new Error("Unexpected status");
      setStatsCandidates({ data: response.data, isLoading: false, error: null });
    } catch (err) {
      console.error("Stats candidates error:", err);
      setStatsCandidates((prev) => ({
        ...prev,
        isLoading: false,
        error: "Impossible de charger les analyses.",
      }));
    }
  }, []);

  // ---------------------- analyses list ---------------------------
  const fetchAnalyses = useCallback(async (filters = {}) => {
    lastFilters.current = filters;
    setAnalyses((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await analysesHistoryApi.getAnalysesListApi(filters);
      if (response.status !== 200) throw new Error("Unexpected status");
      setAnalyses({ data: response.data, isLoading: false, error: null });
    } catch (err) {
      console.error("Analyses list error:", err);
      setAnalyses((prev) => ({
        ...prev,
        isLoading: false,
        error: "Impossible de charger les analyses.",
      }));
    }
  }, []);

  // ---------------------- delete an analysis ---------------------------
  const deleteAnalysis = useCallback(
    async (item) => {
      try {
        const response = await analysesHistoryApi.deleteAnalysisByIdApi(item.analysis_id);
        if (response.status !== 200 && response.status !== 204)
          throw new Error("Unexpected status");

        onAnalysisDeleted?.(item);
        fetchAnalyses(lastFilters.current); // refresh, keeping the current filters
      } catch (err) {
        console.error("Delete analysis error:", err);
        alert("Impossible de supprimer cette analyse.");
      }
    },
    [onAnalysisDeleted, fetchAnalyses],
  );

  return {
    stats,
    statsIds,
    fetchStats,
    fetchStatsByIds,
    statsCandidates,
    fetchStatsCandidates,
    analyses,
    fetchAnalyses,
    deleteAnalysis,
  };
};

export default useAnalysesHistory;
